import { useEffect, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Switch } from 'react-native-paper';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

interface ToggleProps {
  enabled: boolean;
  onToggle: () => void;
}

const TRACK_WIDTH = 51;
const TRACK_HEIGHT = 31;
const BORDER_WIDTH = 1;
const THUMB_INSET = 2;
const INNER_WIDTH = TRACK_WIDTH - BORDER_WIDTH * 2;
const THUMB_SIZE = TRACK_HEIGHT - THUMB_INSET * 2;

export default function Toggle({ enabled, onToggle }: ToggleProps) {
  const { styles, theme } = useStyles(stylesheet);

  const [toggled, setToggled] = useState(enabled);

  useEffect(() => {
    setToggled(enabled);
  }, [enabled]);

  if (Platform.OS === 'web') {
    const thumbLeft = enabled
      ? INNER_WIDTH - THUMB_SIZE - THUMB_INSET
      : THUMB_INSET;

    return (
      <Pressable
        onPress={onToggle}
        accessibilityRole="switch"
        accessibilityState={{ checked: enabled }}
        style={[
          styles.webTrack,
          {
            backgroundColor: enabled
              ? theme.colors.toggle.active.backgroundColor
              : theme.colors.toggle.default.backgroundColor,
            borderColor: enabled
              ? theme.colors.toggle.active.border
              : theme.colors.toggle.default.border,
          },
        ]}
      >
        <View
          style={[
            styles.webThumb,
            {
              left: thumbLeft,
              backgroundColor: enabled
                ? theme.colors.toggle.active.handle
                : theme.colors.toggle.default.handle,
            },
          ]}
        />
      </Pressable>
    );
  }

  return (
    <Switch
      value={toggled}
      onValueChange={onToggle}
      color={
        toggled
          ? theme.colors.toggle.active.handle
          : theme.colors.toggle.default.handle
      }
      trackColor={{
        false: theme.colors.toggle.default.backgroundColor,
        true: theme.colors.toggle.active.backgroundColor,
      }}
      style={[
        {
          borderWidth: 1,
          borderRadius: 16,
        },
        {
          borderColor: toggled
            ? theme.colors.toggle.active.border
            : theme.colors.toggle.default.border,
        },
      ]}
    />
  );
}

const stylesheet = createStyleSheet(() => ({
  webTrack: {
    width: TRACK_WIDTH,
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    borderWidth: BORDER_WIDTH,
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
  },
  webThumb: {
    position: 'absolute',
    top: THUMB_INSET,
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
  },
}));
